import type { ItemCategory } from "./inventory.ts";

/**
 * The network diagram's vocabulary (network diagram spec §3 and §6). One list for the server, the web app and a
 * later document renderer: the icon keys, what a kind maps to, and what counts as infrastructure.
 */
export const DIAGRAM_ICON_KEYS = [
  "router",
  "firewall",
  "switch",
  "access-point",
  "server",
  "nas",
  "printer",
  "desktop",
  "laptop",
  "tablet",
  "phone",
  "ip-phone",
  "camera",
  "tv",
  "ups",
  "internet",
  "modem",
  "cloud",
  "group",
  "vpn",
  "other",
] as const;
export type DiagramIconKey = (typeof DIAGRAM_ICON_KEYS)[number];

const ICON_KEYS: ReadonlySet<string> = new Set(DIAGRAM_ICON_KEYS);
export const isDiagramIconKey = (value: string): value is DiagramIconKey => ICON_KEYS.has(value);

export const DIAGRAM_NODE_TYPES = ["item", "internet", "modem", "cloud", "vpn", "group"] as const;
export type DiagramNodeType = (typeof DIAGRAM_NODE_TYPES)[number];
/** A node that is not an item. */
export type DiagramFreeNodeType = Exclude<DiagramNodeType, "item">;

/** The label a free node shows while its own is empty, and its icon while none is chosen. */
export const DIAGRAM_FREE_NODES: Record<DiagramFreeNodeType, { label: string; icon: DiagramIconKey }> = {
  internet: { label: "Internet", icon: "internet" },
  modem: { label: "ISP modem", icon: "modem" },
  cloud: { label: "Cloud service", icon: "cloud" },
  vpn: { label: "VPN", icon: "vpn" },
  group: { label: "Workstations", icon: "group" },
};

export const DIAGRAM_LIMITS = {
  nodes: 200,
  links: 400,
  /** Hand-picked group members in one diagram (device groups spec §5.5). */
  members: 1000,
  nodeLabel: 60,
  linkLabel: 40,
  coordinate: 20000,
} as const;

/**
 * The words that name each icon, in the order they are tried: the first key with a matching word wins, so
 * "Firewall/Router" is a firewall and "Print Server" a server (decision D8 calls every kind with "server" one).
 */
const KIND_WORDS: ReadonlyArray<readonly [DiagramIconKey, readonly string[]]> = [
  ["firewall", ["firewall", "utm"]],
  ["server", ["server", "hypervisor"]],
  ["router", ["router", "gateway"]],
  ["switch", ["switch"]],
  ["printer", ["printer", "copier", "scanner", "plotter", "multifunction", "mfp"]],
  ["access-point", ["access point", "accesspoint", "wireless", "wifi", "wi-fi", "ap"]],
  ["nas", ["nas", "san", "storage"]],
  ["ups", ["ups"]],
  ["camera", ["camera", "cctv", "nvr"]],
  ["ip-phone", ["desk phone", "ip phone", "voip"]],
  ["tablet", ["tablet", "ipad"]],
  ["phone", ["smartphone", "iphone", "phone"]],
  ["laptop", ["laptop", "notebook", "macbook"]],
  ["desktop", ["desktop", "workstation", "computer", "imac", "pc"]],
  // Not a screen: a sensor box that has "monitor" in its name.
  ["other", ["environment", "sensor"]],
  ["tv", ["display", "screen", "monitor", "television", "tv"]],
];

/**
 * A word this short is matched as a whole word: "ap" is in "laptop", "ups" in "upstairs". So is "phone", which
 * "headphone", "microphone" and "speakerphone" contain (a "Smartphone" has its own word).
 */
const WHOLE_WORD_MAX = 3;
const WHOLE_WORDS: ReadonlySet<string> = new Set(["phone"]);
const isWholeWord = (n: string): boolean => n.length <= WHOLE_WORD_MAX || WHOLE_WORDS.has(n);

export function iconForKind(kind: string): DiagramIconKey {
  const text = kind.toLowerCase();
  const words = new Set(text.split(/[^a-z0-9]+/));
  for (const [key, names] of KIND_WORDS)
    if (names.some((n) => (isWholeWord(n) ? words.has(n) : text.includes(n)))) return key;
  return "other";
}

export const isServerKind = (kind: string): boolean => kind.toLowerCase().includes("server");

/** The items a diagram is expected to show: network equipment (printers are category network, so they count), and the devices that are servers. */
export const isInfrastructureItem = (item: { category: ItemCategory; kind: string }): boolean =>
  item.category === "network" || isServerKind(item.kind);
